// ============================================================
// AmanChain — Network Essentials registry
// The canonical asset set every global platform expects on a
// serious chain: dollar units (USDT/USDC) + wrapped majors
// (BTC/ETH/SOL/BNB). Deployment itself happens ON-CHAIN by the
// network issuer through real consensus transactions
// (token_create + pool_create) — this module only curates the
// canonical list for the interface and documentation layers.
// ============================================================

export type EssentialKind = 'stable' | 'wrapped';

export interface EssentialAsset {
  symbol: string;
  name: string;
  kind: EssentialKind;
  color: string;        // brand color for chips/accents
  decimals: number;     // AMT-20 decimals used at deployment (0-8)
  blurb: string;
}

export const ESSENTIALS: EssentialAsset[] = [
  {
    symbol: 'USDT', name: 'Tether USD (AmanChain)', kind: 'stable', color: '#26A17B', decimals: 6,
    blurb: 'Network dollar unit. On AmanChain testnet it is issued as a 100% AMAN-collateral stable — minted only against escrowed AMAN and redeemable at the peg inside consensus.',
  },
  {
    symbol: 'USDC', name: 'USD Coin (AmanChain)', kind: 'stable', color: '#2775CA', decimals: 6,
    blurb: '100% AMAN-collateralized stable. Every unit is minted against AMAN locked in the consensus vault and can always be redeemed at the peg — the collateral invariant is enforced by the chain itself.',
  },
  {
    symbol: 'BTC', name: 'Bitcoin (AmanChain)', kind: 'wrapped', color: '#F7931A', decimals: 8,
    blurb: 'Bitcoin-anchored asset of the AmanChain economy — 8 decimals like native BTC, priced by real AMM reserves in the network DEX.',
  },
  {
    symbol: 'ETH', name: 'Ethereum (AmanChain)', kind: 'wrapped', color: '#627EEA', decimals: 8,
    blurb: 'Ethereum-anchored asset of the AmanChain economy — priced by real AMM reserves in the network DEX.',
  },
  {
    symbol: 'SOL', name: 'Solana (AmanChain)', kind: 'wrapped', color: '#9945FF', decimals: 8,
    blurb: 'Solana-anchored asset of the AmanChain economy — priced by real AMM reserves in the network DEX.',
  },
  {
    symbol: 'BNB', name: 'BNB (AmanChain)', kind: 'wrapped', color: '#F3BA2F', decimals: 8,
    blurb: 'BNB-anchored asset of the AmanChain economy — priced by real AMM reserves in the network DEX.',
  },
];

export const ESSENTIAL_MAP: Record<string, EssentialAsset> = Object.fromEntries(
  ESSENTIALS.map(a => [a.symbol, a]),
);

/** True when the symbol belongs to the canonical network asset set */
export const isEssential = (symbol: string): boolean => Boolean(ESSENTIAL_MAP[symbol]);
